import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StartPage, TopProgress, SearchComponent, FilterBar, Button } from "../../../components";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { useUi } from "../../../context/ui.context";
import { hypervisorService } from "../../../api";
import { useNavigate } from "react-router";
import {
  StateWrapper,
  GreenDot,
  Greentext,
} from './styles';
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { usePageTitle } from "../../../context/navigationContext";

const HYPERVISOR_FILTER_KEYS = ['status', 'state', 'region', 'hypervisor_type', 'created_at'];
const DEFAULT_ORDERING = '-created_at';

interface Hypervisor {
  id: string;
  host_name: string;
  status: string;
  state: string;
  region: string;
  host_ip: string;
  hypervisor_type: string;
  hypervisor_version: number;
  vcpus: number;
  vcpus_used: number;
  memory_mb: number | number[]; // может прийти как массив
  memory_mb_used: number;
  local_gb: number;
  local_gb_used: number;
  running_vms: number;
  cpu_info: string;
  current_workload: number;
  disk_available_least: number;
  free_disk_gb: number;
  free_ram_mb: number;
}

const Hypervisors: FC = () => {
  const [hypervisors, setHypervisors] = useState<any[]>([]);
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);

  const [paginationData, setPaginationData] = useState<any>();
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState<any>()
  const normalizedFilterOptions = useMemo(() => {
    if (!filterOptions) return undefined;
    return HYPERVISOR_FILTER_KEYS.reduce((acc:any, key) => {
      if (filterOptions?.[key]?.length) {
        acc[key] = filterOptions[key];
      }
      return acc;
    }, {});
  }, [filterOptions]);
  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
    setAllChecked(false);
  }

  const { viewAlert } = useUi();
  const [t] = useTranslation();
  usePageTitle(t("PAGE_TITLE_HYPERVISORS"));
  const navigate = useNavigate();
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);

  const _updateHypervisors = () => {
    hypervisorService.getHypervisors({
      page: page || 1,
      ordering: ordering || DEFAULT_ORDERING,
      pageSize: pageSize || 10,
      search: searchString || '',
      filtering: filtering || '',
    })
      .then((res: any) => {
        setPaginationData(res.data);
        setFilterOptions(res?.data?.filter_options);
        setHypervisors(res.data.objects.map((hypervisor: any) => {
          // memory_mb может прийти как массив, берем первый элемент
          const memoryMb = Array.isArray(hypervisor.memory_mb) ? hypervisor.memory_mb[0] : hypervisor.memory_mb;

          return {
            id: hypervisor.id,
            host_name: hypervisor.host_name,
            hypervisor_status: hypervisor.status, // переименовано чтобы избежать конфликта с styled-components
            hypervisor_state: hypervisor.state, // переименовано чтобы избежать конфликта
            region: hypervisor.region,
            host_ip: hypervisor.host_ip,
            hypervisor_type: hypervisor.hypervisor_type,
            hypervisor_version: hypervisor.hypervisor_version,
            vcpus: hypervisor.vcpus,
            vcpus_used: hypervisor.vcpus_used,
            memory_mb: memoryMb,
            memory_mb_used: hypervisor.memory_mb_used,
            local_gb: hypervisor.local_gb,
            local_gb_used: hypervisor.local_gb_used,
            running_vms: hypervisor.running_vms,
            cpu_info: hypervisor.cpu_info,
            current_workload: hypervisor.current_workload,
            disk_available_least: hypervisor.disk_available_least,
            free_disk_gb: hypervisor.free_disk_gb,
            free_ram_mb: hypervisor.free_ram_mb,
            created_at: timeConverterMMDDYYHHMM(hypervisor.created_at),
            navigate: `/openstack/hypervisors/${hypervisor.id}`,
            name: hypervisor.host_name,
            state_display: (
              <StateWrapper>
                <GreenDot active={hypervisor.state === "up"} />
                <Greentext active={hypervisor.state === "up"}>
                  {hypervisor.state === "up" ? t("UP") : t("DOWN")}
                </Greentext>
              </StateWrapper>
            ),
            enabled: hypervisor.status === "enabled" ? t("ENABLED") : t("DISABLED"),
            status: undefined, // явно очищаем чтобы избежать конфликта
            state: undefined, // явно очищаем чтобы избежать конфликта
            type: hypervisor.hypervisor_type,
            vcpus_info: `${hypervisor.vcpus_used} / ${hypervisor.vcpus}`,
            memory_info: `${hypervisor.memory_mb_used} / ${memoryMb} MB`,
            disk_info: `${hypervisor.local_gb_used} / ${hypervisor.local_gb} GB`,
          }
        }));
      })
      .catch((e) => {
        viewAlert({
          severity: 'error',
          message: e?.response?.data?.detail || `${t("ERROR_OCCURRED")}`,
        });
        navigate('/home');
      })
      .finally(() => {
        setPageLoading(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    _updateHypervisors();
    const interval = setInterval(() => {
      _updateHypervisors();
    }, 10000);
    return () => {
      clearInterval(interval);
    };
  }, [
    page,
    ordering,
    pageSize,
    searchString,
    filtering
  ]);
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])

  const Columns = [
    {
      title: t("NAME"),
      key: 'name'
    },
    {
      title: t("STATE"),
      key: 'state_display'
    },
    {
      title: t("ENABLED"),
      key: 'enabled'
    },
    {
      title: t("REGION"),
      key: 'region',
      sorted:true,
    },
    {
      title: t("HOST_IP"),
      key: 'host_ip'
    },
    {
      title: t("TYPE"),
      key: 'type'
    },
    {
      title: t("VCPUS"),
      key: 'vcpus_info'
    },
    {
      title: t("MEMORY"),
      key: 'memory_info'
    },
    {
      title: t("DISK"),
      key: 'disk_info'
    },
    {
      title: t("CREATED_AT"),
      key: 'created_at',
      sorted:true,
    },
    {
      title: t("VMS"),
      key: 'running_vms'
    },
  ];

  return (
    <>
      <TopProgress loading={loading} />
      <StartPage
        title={`${t("HYPERVISORS")}`}
        noResourceText={`${t("NO_HYPERVISORS")}`}
        haveResource={hypervisors?.length > 0}
        pageLoading={pageLoading}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginLeft:'auto'}}>
                <Button
                  title={t("CLEAR_FILTERS")}
                  onClick={handleResetFilters}
                  variant='stroke'
                  size='display'
                  disabled={
                    (!filtering || filtering === '') &&
                    !searchString &&
                    ordering === DEFAULT_ORDERING
                  }
                />
              </div>
            </div>
            {normalizedFilterOptions ? (
              <FilterBar filterOptions={normalizedFilterOptions} />
            ) : null}
          </div>
        }
        tableData={{
          columns: Columns,
          data: hypervisors,
          paginationData: paginationData,
          updateData: _updateHypervisors,
          hideCheckbox:true
        }}
        hideCreate
        hideQuotas
      />
    </>
  );
};

export default Hypervisors;
